import {
  Component,
  ComponentPublicInstance,
  createApp,
  WatchCallback,
  WatchSource,
  WatchStopHandle,
} from "vue";
import Oruga from "@oruga-ui/oruga-next";
import { bulmaConfig } from "@oruga-ui/theme-bulma";
import { createRouter, createWebHistory } from "vue-router";
import "@oruga-ui/theme-bulma/dist/bulma.css";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import App from "./App.vue";
import searchComponent from "./components/search/citation-search.vue";
import searchResultsComponent from "./components/search/citation-search-results.vue";
import paymentLoginCheck from "./components/payment/payment-login-check.vue";
import paymentConfirmationComponent from "./components/payment/PaymentConfirmationPage.vue";
import cartComponent from "./components/cart/ShoppingCart.vue";
import checkoutComponent from "./components/payment/CheckoutPage.vue";

import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { vMaska } from "maska";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faSearch,
  faCircleSmall,
  faCircleInfo,
  faTimes,
  faCheck,
  faArrowUp,
  faExclamationCircle,
  faCircleX,
  faEnvelope,
  faEye,
  faEyeSlash,
  faCircleCheck,
  faSpinner,
  faSyncAlt,
  faArrowLeft,
  faCircleNotch,
} from "@fortawesome/pro-solid-svg-icons";
import "devextreme/dist/css/dx.light.css";
import "@devexpress/analytics-core/dist/css/dx-analytics.common.css";
import "@devexpress/analytics-core/dist/css/dx-analytics.light.css";
import "@devexpress/analytics-core/dist/css/dx-querybuilder.css";
import "devexpress-reporting/dist/css/dx-webdocumentviewer.css";
import "devexpress-reporting/dist/css/dx-reportdesigner.css";
import axios from "./plugins/axios.plugin";
import VueCountdown from "@chenfengyuan/vue-countdown";
import globalProgressCircularLoaderPlugin from "@/plugins/progressCircularLoaderPlugin";
import globalSnackbarPlugin from "@/plugins/snackbarPlugin";
import currencyjs from "@/plugins/currencyjs.plugin";

// Vuetify
import "vuetify/styles";
import "@mdi/font/css/materialdesignicons.css";
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import { VMaskInput } from 'vuetify/labs/VMaskInput'

const vuetify = createVuetify({
  components,
  directives,
});

library.add(
  faSearch,
  faCircleSmall,
  faCircleInfo,
  faTimes,
  faCheck,
  faArrowUp,
  faExclamationCircle,
  faArrowLeft,
  faCircleX,
  faEnvelope,
  faEye,
  faEyeSlash,
  faCircleCheck,
  faSpinner,
  faSyncAlt,
  faCircleNotch,
);

bulmaConfig.iconComponent = "vue-fontawesome";
bulmaConfig.iconPack = "fas";

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

//Route
const routes = [
  { path: "/", name: "search", component: searchComponent },
  { path: "/results", name: "results", component: searchResultsComponent },
  {
    path: "/payment-login",
    name: "paymentLoginCheck",
    component: paymentLoginCheck,
  },
  {
    path: "/payment-confirmation/",
    name: "paymentConfirmation",
    component: paymentConfirmationComponent,
    props: (route: any) => ({
      confirmationNumber: route.query.confirmationNumber,
      token: route.query.token
    }),
  },
  { path: "/cart", name: "cart", component: cartComponent },
  { path: "/checkout", name: "checkout", component: checkoutComponent },
];

type formWithFormWatcherAndFormChanges = {
  FormChanges: Array<string>;
  FormWatchers: Array<WatchStopHandle>;
} & {
  [x: string | number | symbol]: unknown;
};

interface functions {
  formWatch: (
    form: formWithFormWatcherAndFormChanges,
    componentPublicInstance: ComponentPublicInstance,
    removeWatch: boolean,
  ) => void;
}

const formWatch = function (
  form: formWithFormWatcherAndFormChanges,
  componentPublicInstance: ComponentPublicInstance,
  removeWatch: boolean,
): void {
  if (!removeWatch) {
    form.FormChanges = [];
    form.FormWatchers = [];

    Object.keys(form).forEach((key) => {
      if (key !== "FormChanges" && key !== "FormWatchers") {
        const unwatch = componentPublicInstance.$watch(
          () => form[key],
          () => {
            const exists: boolean = form.FormChanges.includes(`${key}`);

            if (!exists) {
              form.FormChanges.push(key);
            }
          },
        );

        form.FormWatchers.push(unwatch);
      }
    });
  } else {
    for (let i = 0; i < form.FormWatchers.length; i++) {
      form.FormWatchers[i]();
    }
  }
};

const functions: functions = {
  formWatch: formWatch,
};

const baseURL = process.env.VUE_APP_BASE_URL;
const apiURL = process.env.VUE_APP_API_BASE_URL;

const router = createRouter({
  history: createWebHistory(baseURL),
  routes,
});

const app = createApp(App);

app.provide("$functions", functions);

app.component(VueCountdown.name || "", VueCountdown);

app
  .component("vue-fontawesome", FontAwesomeIcon)
  .component("v-mask-input", VMaskInput)
  .use(router)
  .use(Oruga, bulmaConfig)
  .use(vuetify)
  .use(pinia)
  .use(axios, {
    baseUrl: apiURL,
  })
  .use(currencyjs)
  .use(globalSnackbarPlugin)
  .use(globalProgressCircularLoaderPlugin)
  .mount("#app");

app.directive("maska", vMaska);
